import { FileWithPreview } from 'react-dropzone';

import { ImageGalleryType } from '../../components/ImageGallery/ImageGallery';
import { imageUploadTextErrorMapper } from '../../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { BannerId, ImageEditorId, ImageItem } from '../../typings/banners';
import { ValidationErrorData, ValidationResultItem } from '../../typings/common';
import { BannerImageForEdit, BannerImageType } from '../../typings/domain';
import { ImageEditorsState, ImageEditorState } from '../../typings/state';

export const REMOVE_BANNER_IMAGE = 'BANNER_IMAGE_REMOVE';
export type REMOVE_BANNER_IMAGE = typeof REMOVE_BANNER_IMAGE;
export interface BannerImageRemove {
    type: REMOVE_BANNER_IMAGE;
    bannerId: BannerId;
}
export function bannerImageRemove(bannerId: BannerId): BannerImageRemove {
    return {
        type: REMOVE_BANNER_IMAGE,
        bannerId
    };
}

export const SHOW_BANNER_IMAGE_LINK_INPUT = 'SHOW_BANNER_IMAGE_LINK_INPUT';
export type SHOW_BANNER_IMAGE_LINK_INPUT = typeof SHOW_BANNER_IMAGE_LINK_INPUT;
export interface ShowBannerImageLinkInput {
    type: SHOW_BANNER_IMAGE_LINK_INPUT;
    bannerId: BannerId;
}
export function showBannerImageLinkInput(bannerId: BannerId): ShowBannerImageLinkInput {
    return {
        type: SHOW_BANNER_IMAGE_LINK_INPUT,
        bannerId
    };
}

export const UPLOAD_BANNER_IMAGE = 'UPLOAD_BANNER_IMAGE';
export type UPLOAD_BANNER_IMAGE = typeof UPLOAD_BANNER_IMAGE;
export interface UploadBannerImage {
    type: UPLOAD_BANNER_IMAGE;
    bannerId: BannerId;
    filesToUpload: FileWithPreview[];
    isLocalUploading?: boolean;
}
export function uploadBannerImage(
    bannerId: BannerId,
    files: FileWithPreview[],
    isLocalUploading?: boolean
): UploadBannerImage {
    return {
        type: UPLOAD_BANNER_IMAGE,
        bannerId,
        filesToUpload: files,
        isLocalUploading
    };
}

export const CANCEL_UPLOAD_BANNER_IMAGE = 'CANCEL_UPLOAD_BANNER_IMAGE';
export type CANCEL_UPLOAD_BANNER_IMAGE = typeof CANCEL_UPLOAD_BANNER_IMAGE;
export interface CancelUploadBannerImage {
    type: CANCEL_UPLOAD_BANNER_IMAGE;
    bannerId: BannerId;
}
export function cancelUploadBannerImage(bannerId: BannerId): CancelUploadBannerImage {
    return {
        type: CANCEL_UPLOAD_BANNER_IMAGE,
        bannerId
    };
}

export const CANCEL_UPLOAD_IMAGE_CREATIVE = 'CANCEL_UPLOAD_IMAGE_CREATIVE';
export type CANCEL_UPLOAD_IMAGE_CREATIVE = typeof CANCEL_UPLOAD_IMAGE_CREATIVE;
export interface CancelUploadImageCreative {
    type: CANCEL_UPLOAD_IMAGE_CREATIVE;
    bannerId: BannerId;
}
export function cancelUploadImageCreative(bannerId: BannerId): CancelUploadImageCreative {
    return {
        type: CANCEL_UPLOAD_IMAGE_CREATIVE,
        bannerId
    };
}

export const UPDATE_BANNER_IMAGE = 'UPDATE_BANNER_IMAGE';
export type UPDATE_BANNER_IMAGE = typeof UPDATE_BANNER_IMAGE;
export interface UpdateBannerImage {
    type: UPDATE_BANNER_IMAGE;
    bannerId: BannerId;
    image: BannerImageForEdit;
}

export function updateBannerImage(bannerId: BannerId, image: BannerImageForEdit): UpdateBannerImage {
    return {
        type: UPDATE_BANNER_IMAGE,
        bannerId: bannerId,
        image
    };
}

export const ADD_BANNER_IMAGE = 'ADD_BANNER_IMAGE';
export type ADD_BANNER_IMAGE = typeof ADD_BANNER_IMAGE;
export interface AddBannerImage {
    type: ADD_BANNER_IMAGE;
    data: Record<ImageEditorId, ImageEditorState>;
}
export function addBannerImage(data: Record<ImageEditorId, ImageEditorState>): AddBannerImage {
    return {
        type: ADD_BANNER_IMAGE,
        data
    };
}

export const RESET_BANNER_IMAGE_UPLOAD_ERROR = 'RESET_BANNER_IMAGE_UPLOAD_ERROR';
export type RESET_BANNER_IMAGE_UPLOAD_ERROR = typeof RESET_BANNER_IMAGE_UPLOAD_ERROR;
export interface ResetBannerImageUploadError {
    type: RESET_BANNER_IMAGE_UPLOAD_ERROR;
    bannerId: BannerId;
}
export function resetBannerImageUploadError(bannerId: BannerId): ResetBannerImageUploadError {
    return {
        type: RESET_BANNER_IMAGE_UPLOAD_ERROR,
        bannerId
    };
}

export const SHOW_BANNER_IMAGE_UPLOAD_ERROR = 'SHOW_BANNER_IMAGE_UPLOAD_ERROR';
export type SHOW_BANNER_IMAGE_UPLOAD_ERROR = typeof SHOW_BANNER_IMAGE_UPLOAD_ERROR;
export interface ShowBannerImageUploadError {
    type: SHOW_BANNER_IMAGE_UPLOAD_ERROR;
    bannerId: BannerId;
    errors: ValidationResultItem[];
    isUploadByLink: boolean;
    isMassAction: boolean;
}
export function showBannerImageUploadError(
    bannerId: BannerId,
    errors: ValidationResultItem[],
    isUploadByLink: boolean = false,
    isMassAction: boolean = false
): ShowBannerImageUploadError {
    return {
        type: SHOW_BANNER_IMAGE_UPLOAD_ERROR,
        bannerId,
        errors,
        isUploadByLink,
        isMassAction
    };
}

export const RESET_BANNER_IMAGE_CREATIVE_MODE = 'RESET_BANNER_IMAGE_CREATIVE_MODE';
export type RESET_BANNER_IMAGE_CREATIVE_MODE = typeof RESET_BANNER_IMAGE_CREATIVE_MODE;

export interface ResetBannerImageCreativeMode {
    type: RESET_BANNER_IMAGE_CREATIVE_MODE;
    bannerId: BannerId;
}

export function resetBannerImageCreativeMode(bannerId: BannerId): ResetBannerImageCreativeMode {
    return {
        type: RESET_BANNER_IMAGE_CREATIVE_MODE,
        bannerId
    };
}

export const SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR = 'SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR';
export type SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR = typeof SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR;
export interface ShowBannerImageCreativeUploadError {
    type: SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR;
    bannerId: BannerId;
    errors: ValidationResultItem[];
    isUploadByLink?: boolean;
}
export function showBannerImageCreativeUploadError(
    bannerId: BannerId,
    errors: ValidationResultItem[],
    isUploadByLink?: boolean
): ShowBannerImageCreativeUploadError {
    return {
        type: SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR,
        bannerId,
        errors,
        isUploadByLink
    };
}

export const SHOW_BANNER_IMAGES_UPLOAD_ERROR = 'SHOW_BANNER_IMAGES_UPLOAD_ERROR';
export type SHOW_BANNER_IMAGES_UPLOAD_ERROR = typeof SHOW_BANNER_IMAGES_UPLOAD_ERROR;
export interface ShowBannerImagesUploadError {
    type: SHOW_BANNER_IMAGES_UPLOAD_ERROR;
    bannerId: BannerId;
    errors: Record<string, ValidationErrorData[]>;
}
export function showBannerImagesUploadError(
    bannerId: BannerId,
    errors: Record<string, ValidationResultItem[]>
): ShowBannerImagesUploadError {
    return {
        type: SHOW_BANNER_IMAGES_UPLOAD_ERROR,
        bannerId,
        errors: Object.keys(errors).reduce((result, key) => {
            result[key] = errors[key].map(error => ({
                code: error.code,
                path: error.path,
                text: imageUploadTextErrorMapper(error)
            }));

            return result;
        }, {})
    };
}

export const UPLOAD_BANNER_IMAGE_BY_LINK = 'UPLOAD_BANNER_IMAGE_BY_LINK';
export type UPLOAD_BANNER_IMAGE_BY_LINK = typeof UPLOAD_BANNER_IMAGE_BY_LINK;
export interface UploadBannerImageByLink {
    type: UPLOAD_BANNER_IMAGE_BY_LINK;
    link: string;
    bannerId: string;
}
export function uploadBannerImageByLink(link: string, bannerId: string): UploadBannerImageByLink {
    return {
        type: UPLOAD_BANNER_IMAGE_BY_LINK,
        link,
        bannerId
    };
}

export const UPDATE_BANNER_IMAGE_BY_LINK = 'UPDATE_BANNER_IMAGE_BY_LINK';
export type UPDATE_BANNER_IMAGE_BY_LINK = typeof UPDATE_BANNER_IMAGE_BY_LINK;
export interface UpdateBannerImageByLink {
    type: UPDATE_BANNER_IMAGE_BY_LINK;
    link: string;
    name: string;
    bannerId: string;
    isCropNeeded: boolean;
}
export function updateBannerImageByLink(
    link: string,
    name: string,
    bannerId: string,
    isCropNeeded: boolean
): UpdateBannerImageByLink {
    return {
        type: UPDATE_BANNER_IMAGE_BY_LINK,
        link,
        bannerId,
        name,
        isCropNeeded
    };
}

export const SHOW_IMAGE_GALLERY = 'SHOW_IMAGE_GALLERY';
export type SHOW_IMAGE_GALLERY = typeof SHOW_IMAGE_GALLERY;

export interface ShowImageGallery {
    type: SHOW_IMAGE_GALLERY;
    bannerId: BannerId;
    galleryType: ImageGalleryType;
}

export function showImageGallery(bannerId: BannerId, galleryType: ImageGalleryType): ShowImageGallery {
    return {
        type: SHOW_IMAGE_GALLERY,
        bannerId,
        galleryType
    };
}

export const UPDATE_IMAGES_FOR_GALLERY_EDIT = 'UPDATE_IMAGES_FOR_GALLERY_EDIT';
export type UPDATE_IMAGES_FOR_GALLERY_EDIT = typeof UPDATE_IMAGES_FOR_GALLERY_EDIT;

export interface UpdateImagesForGalleryEdit {
    type: UPDATE_IMAGES_FOR_GALLERY_EDIT;
    bannerId: BannerId;
    images: ImageItem[];
}

export function updateImagesForGalleryEdit(bannerId: BannerId, images: ImageItem[]): UpdateImagesForGalleryEdit {
    return {
        type: UPDATE_IMAGES_FOR_GALLERY_EDIT,
        bannerId,
        images
    };
}

export const CLOSE_IMAGES_GALLERY = 'CLOSE_IMAGES_GALLERY';
export type CLOSE_IMAGES_GALLERY = typeof CLOSE_IMAGES_GALLERY;

export interface CloseImagesGallery {
    type: CLOSE_IMAGES_GALLERY;
    bannerId: BannerId;
}

export function closeImagesGallery(bannerId: BannerId): CloseImagesGallery {
    return {
        type: CLOSE_IMAGES_GALLERY,
        bannerId
    };
}

export const VALIDATE_BANNER_IMAGE_FROM_GALLERY = 'VALIDATE_BANNER_IMAGE_FROM_GALLERY';
export type VALIDATE_BANNER_IMAGE_FROM_GALLERY = typeof VALIDATE_BANNER_IMAGE_FROM_GALLERY;

export interface ValidateBannerImageFromGallery {
    type: VALIDATE_BANNER_IMAGE_FROM_GALLERY;
    bannerId: BannerId;
    imagesHash: string[];
}

export function validateBannerImageFromGallery(
    bannerId: BannerId,
    imagesHash: string[]
): ValidateBannerImageFromGallery {
    return {
        type: VALIDATE_BANNER_IMAGE_FROM_GALLERY,
        bannerId,
        imagesHash
    };
}

export const UPDATE_BANNER_IMAGE_BY_GALLERY = 'UPDATE_BANNER_IMAGE_BY_GALLERY';
export type UPDATE_BANNER_IMAGE_BY_GALLERY = typeof UPDATE_BANNER_IMAGE_BY_GALLERY;

export interface UpdateBannerImageByGallery {
    type: UPDATE_BANNER_IMAGE_BY_GALLERY;
    bannerId: BannerId;
    imagesHash: string[];
    bannerType: BannerImageType;
}

export function updateBannerImageByGallery(
    bannerId: BannerId,
    imagesHash: string[],
    bannerType: BannerImageType
): UpdateBannerImageByGallery {
    return {
        type: UPDATE_BANNER_IMAGE_BY_GALLERY,
        bannerId,
        imagesHash,
        bannerType
    };
}

export const SELECT_IMAGES_IN_GALLERY = 'SELECT_IMAGES_IN_GALLERY';
export type SELECT_IMAGES_IN_GALLERY = typeof SELECT_IMAGES_IN_GALLERY;

export interface SelectImagesInGallery {
    type: SELECT_IMAGES_IN_GALLERY;
    bannerId: BannerId;
    imageHash: string;
}

export function selectImagesInGallery(bannerId: BannerId, imageHash: string): SelectImagesInGallery {
    return {
        type: SELECT_IMAGES_IN_GALLERY,
        bannerId,
        imageHash
    };
}

export const SHOW_IMAGE_GALLERY_ERROR = 'SHOW_IMAGE_GALLERY_ERROR';
export type SHOW_IMAGE_GALLERY_ERROR = typeof SHOW_IMAGE_GALLERY_ERROR;

export interface ShowImageGalleryError {
    type: SHOW_IMAGE_GALLERY_ERROR;
    bannerId: BannerId;
    errors: ValidationResultItem[];
}

export function showImageGalleryError(bannerId: BannerId, errors: ValidationResultItem[]): ShowImageGalleryError {
    return {
        type: SHOW_IMAGE_GALLERY_ERROR,
        bannerId,
        errors
    };
}

export const CLOSE_IMAGE_CROP = 'CLOSE_IMAGE_CROP';
export type CLOSE_IMAGE_CROP = typeof CLOSE_IMAGE_CROP;
export interface CloseImageCrop {
    type: CLOSE_IMAGE_CROP;
    bannerId: BannerId;
}
export function closeImageCrop(bannerId: BannerId): CloseImageCrop {
    return {
        type: CLOSE_IMAGE_CROP,
        bannerId
    };
}

export const SELECT_ONE_IMAGE_IN_GALLERY = 'SELECT_ONE_IMAGE_IN_GALLERY';
export type SELECT_ONE_IMAGE_IN_GALLERY = typeof SELECT_ONE_IMAGE_IN_GALLERY;

export interface SelectOneImageInGallery {
    type: SELECT_ONE_IMAGE_IN_GALLERY;
    bannerId: BannerId;
    imageHash: string;
}

export function selectOneImageInGallery(bannerId: BannerId, imageHash: string): SelectOneImageInGallery {
    return {
        type: SELECT_ONE_IMAGE_IN_GALLERY,
        bannerId,
        imageHash
    };
}

export const SELECT_IMAGE_CANVAS_IDS = 'SELECT_IMAGE_CANVAS_IDS';
export type SELECT_IMAGE_CANVAS_IDS = typeof SELECT_IMAGE_CANVAS_IDS;

export interface SelectImageCanvasIds {
    type: SELECT_IMAGE_CANVAS_IDS;
    bannerId: BannerId;
    ids: string[];
}

export function selectImageCanvasIds(bannerId: BannerId, ids: string[]): SelectImageCanvasIds {
    return {
        type: SELECT_IMAGE_CANVAS_IDS,
        bannerId,
        ids
    };
}

export const UPDATE_BANNER_IMAGE_CREATIVE = 'UPDATE_BANNER_IMAGE_CREATIVE';
export type UPDATE_BANNER_IMAGE_CREATIVE = typeof UPDATE_BANNER_IMAGE_CREATIVE;

export interface UpdateBannerCanvasCreative {
    type: UPDATE_BANNER_IMAGE_CREATIVE;
    bannerId: BannerId;
    image: BannerImageForEdit;
}

export function updateBannerCanvasCreative(bannerId: BannerId, image: BannerImageForEdit): UpdateBannerCanvasCreative {
    return {
        type: UPDATE_BANNER_IMAGE_CREATIVE,
        bannerId,
        image
    };
}

export const UPDATE_IMAGE_EDITOR = 'UPDATE_IMAGE_EDITOR';
export type UPDATE_IMAGE_EDITOR = typeof UPDATE_IMAGE_EDITOR;

export interface UpdateImageEditorAction {
    type: UPDATE_IMAGE_EDITOR;
    data: ImageEditorsState;
}

export function updateImageEditor(imageEditorData: ImageEditorsState): UpdateImageEditorAction {
    return {
        type: UPDATE_IMAGE_EDITOR,
        data: imageEditorData
    };
}

export const ADD_IMAGES_TO_NEW_BANNERS = 'ADD_IMAGES_TO_NEW_BANNERS';
export type ADD_IMAGES_TO_NEW_BANNERS = typeof ADD_IMAGES_TO_NEW_BANNERS;

export interface AddImagesToNewBannersAction {
    type: ADD_IMAGES_TO_NEW_BANNERS;
    newImages: ImageItem[] | BannerImageForEdit[];
}

export function addImagesToNewBanners(newImages: ImageItem[] | BannerImageForEdit[]): AddImagesToNewBannersAction {
    return {
        type: ADD_IMAGES_TO_NEW_BANNERS,
        newImages
    };
}

export const CLEAR_IMAGE_EDITOR_DATA = 'CLEAR_IMAGE_EDITOR_DATA';
export type CLEAR_IMAGE_EDITOR_DATA = typeof CLEAR_IMAGE_EDITOR_DATA;

export interface ClearImageEditorDataAction {
    type: CLEAR_IMAGE_EDITOR_DATA;
    bannerId: BannerId;
}

export function clearImageEditorData(bannerId: BannerId): ClearImageEditorDataAction {
    return {
        type: CLEAR_IMAGE_EDITOR_DATA,
        bannerId
    };
}

export const ON_ACCEPT_BULK_IMAGE_REPLACEMENT = 'ON_ACCEPT_BULK_IMAGE_REPLACEMENT';
export type ON_ACCEPT_BULK_IMAGE_REPLACEMENT = typeof ON_ACCEPT_BULK_IMAGE_REPLACEMENT;

export interface OnAcceptBulkImageReplacementAction {
    type: ON_ACCEPT_BULK_IMAGE_REPLACEMENT;
}

export function onAcceptBulkImageReplacement(): OnAcceptBulkImageReplacementAction {
    return {
        type: ON_ACCEPT_BULK_IMAGE_REPLACEMENT
    };
}

export type BannerTextImageActions =
    | ResetBannerImageUploadError
    | AddBannerImage
    | BannerImageRemove
    | UploadBannerImage
    | CancelUploadBannerImage
    | CancelUploadImageCreative
    | ShowBannerImageUploadError
    | UploadBannerImageByLink
    | CloseImageCrop
    | ShowBannerImageLinkInput
    | UpdateBannerImageByLink
    | UpdateBannerImage
    | ShowImageGallery
    | UpdateImagesForGalleryEdit
    | CloseImagesGallery
    | UpdateBannerImageByGallery
    | SelectOneImageInGallery
    | SelectImagesInGallery
    | ShowImageGalleryError
    | ShowBannerImagesUploadError
    | ShowBannerImageCreativeUploadError
    | ValidateBannerImageFromGallery
    | SelectImageCanvasIds
    | UpdateBannerCanvasCreative
    | UpdateImageEditorAction
    | ResetBannerImageCreativeMode
    | AddImagesToNewBannersAction
    | ClearImageEditorDataAction
    | OnAcceptBulkImageReplacementAction;
